import { PlanArea } from "@/tools/floor-area/estimate-plan-area";
import { Box3, Vector3 } from "three";
import { create } from "zustand";

/** The levels of the selection, positions relative to origin */
export type FloorAreaEstimate = PlanArea & { origin: Vector3 };

/** Points sampled to estimate the floor area of the selection */
export type FloorAreaSample = {
  /** Positions relative to origin */
  positions: Float32Array;
  origin: Vector3;
  /** Number of points after each LOD level, to scale the counts to the full resolution */
  levelEnds: number[];
  /** World bounds of the sampled region */
  bounds: Box3;
  /** Identifies the dataset and scans the points were sampled from */
  selectionKey: string;
};

type FloorAreaState = {
  isSampling: boolean;
  sampledPoints: number;
  sample?: FloorAreaSample;
  /** The walkable levels of the region box, positions relative to the origin of the sample */
  estimate?: FloorAreaEstimate;
  isColoredByFloors: boolean;
  /** Index of the level under the mouse in the list */
  hoveredFloor?: number;
  /** Index of the level clicked or tapped in the list */
  pinnedFloor?: number;
  error?: string;
  setColoredByFloors(isColored: boolean): void;
  setHoveredFloor(floor?: number): void;
  togglePinnedFloor(floor: number): void;
};

/** The plan area of the selected region, shared between the 3D scene and the step panel */
export const useFloorAreaStore = create<FloorAreaState>()((set) => ({
  isSampling: false,
  sampledPoints: 0,
  isColoredByFloors: true,
  setColoredByFloors: (isColoredByFloors) => set({ isColoredByFloors }),
  setHoveredFloor: (hoveredFloor) => set({ hoveredFloor }),
  togglePinnedFloor: (floor) =>
    set(({ pinnedFloor }) => ({
      pinnedFloor: pinnedFloor === floor ? undefined : floor,
    })),
}));

/**
 * @param state The floor area state
 * @returns the level to show alone in 3D, the hovered one first
 */
export function selectIsolatedFloor(state: FloorAreaState): number | undefined {
  return state.hoveredFloor ?? state.pinnedFloor;
}

/** Height framed above a floor, to show its walls */
const STOREY_HEIGHT = 3;

/**
 * @param estimate The levels of the selection
 * @param floor Index of the level
 * @returns the world bounds of the floor and of the walls above it
 */
export function computeFloorBounds(
  estimate: FloorAreaEstimate,
  floor: number,
): Box3 {
  const { footprint, heights, elevation } = estimate.levels[floor];
  const { width, cellSize, minX, minZ, data } = footprint;
  const bounds = new Box3();
  const corner = new Vector3();
  data.forEach((isFloor, cell) => {
    if (!isFloor) return;
    const x = minX + (cell % width) * cellSize;
    const z = minZ + Math.floor(cell / width) * cellSize;
    // Filled holes of the floor have no measured height
    const y = Number.isNaN(heights[cell]) ? elevation : heights[cell];
    bounds.expandByPoint(corner.set(x, y, z));
    bounds.expandByPoint(
      corner.set(x + cellSize, y + STOREY_HEIGHT, z + cellSize),
    );
  });
  return bounds.translate(estimate.origin);
}
